import {
  ButtonDS,
  DialogModalDS,
  MessageTooltip,
  SelectDS,
  Spacer,
  WarningOutlined,
} from "@qivia/ui";
import { Option } from "@qivia/ui/src/designSystem/components/Select";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useAppSelector } from "../../../../../redux/hooks";
import { selectCompany } from "../../../homeSlice";
import { selectPlanningMonthlyRows } from "../../../fleet/fleetSlice";
import { selectManagersList } from "../../../users/usersSlice";
import { formatDateWithoutDay } from "../../../../../libSlice/materialUiFormats";
import "../../../../../i18n/i18n";

export const GenerateMonthlyReportModal = (props: {
  visible: boolean;
  onCloseModal: () => void;
}) => {
  const { t } = useTranslation();
  const company = useAppSelector(selectCompany);
  const planningMonthlyRows = useAppSelector(selectPlanningMonthlyRows);
  const managers = useAppSelector(selectManagersList);
  const [selectedPeriodOption, setSelectedPeriodOption] =
    useState<Option<string> | null>(null);
  const [displayError, setDisplayError] = useState(false);

  const periodOptions = useMemo(
    (): Option<string>[] =>
      [...planningMonthlyRows]
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .map((row) => ({
          value: row.date,
          label: formatDateWithoutDay(row.date),
        })),
    [planningMonthlyRows],
  );

  const selectedPlanningRow = useMemo(
    () =>
      planningMonthlyRows.find(
        (row) => row.date === selectedPeriodOption?.value,
      ) ?? null,
    [planningMonthlyRows, selectedPeriodOption],
  );

  const reportRecipientsCount = useMemo(
    () =>
      managers.filter(
        (manager) =>
          manager.status === "ACTIVE" && manager.receiveMileageGapReports,
      ).length,
    [managers],
  );

  const hasRecipients = reportRecipientsCount > 0;

  const canGenerateReport = useMemo(() => {
    return selectedPeriodOption?.value && company && hasRecipients;
  }, [selectedPeriodOption, company, hasRecipients]);

  const handleGenerate = useCallback(() => {
    if (!selectedPeriodOption || !company) {
      setDisplayError(true);
      return;
    }
    console.log({
      date: selectedPeriodOption.value,
      companyUuid: company.uuid,
    });
  }, [company, selectedPeriodOption]);

  const handleClose = useCallback(() => {
    setDisplayError(false);
    setSelectedPeriodOption(null);
    props.onCloseModal();
  }, [props]);

  if (!props.visible || !company) {
    return null;
  }

  return (
    <DialogModalDS
      visible={props.visible}
      onClose={handleClose}
      title={t("fleet.monthlyReports.generateModal.title")}
      width={40}
      bottomButtons={{
        leftButton: (
          <ButtonDS
            text={t("fleet.monthlyReports.generateModal.cancel")}
            format={"fill"}
            buttonType={"secondary"}
            onClick={handleClose}
          />
        ),
        rightButton: (
          <ButtonDS
            text={t("fleet.monthlyReports.generateModal.generate")}
            format={"fill"}
            buttonType={"primary"}
            disabled={!canGenerateReport}
            onClick={handleGenerate}
          />
        ),
      }}
    >
      <StyledColumn>
        <SelectDS
          label={t("fleet.monthlyReports.generateModal.periodLabel")}
          options={periodOptions}
          placeholder={t(
            "fleet.monthlyReports.generateModal.periodPlaceholder",
          )}
          onChange={setSelectedPeriodOption}
          value={selectedPeriodOption}
          allWidth
          isMandatory
          error={
            displayError && !canGenerateReport
              ? t("fleet.monthlyReports.generateModal.error.empty")
              : undefined
          }
        />
        {selectedPeriodOption && selectedPlanningRow && (
          <>
            <StyledInfoText>
              <Spacer x={0.25} />
              {t(
                selectedPlanningRow.planningsRows === 1
                  ? "fleet.monthlyReports.generateModal.planningRowsSingular"
                  : "fleet.monthlyReports.generateModal.planningRows",
                {
                  rowsCount: selectedPlanningRow.planningsRows,
                  period: selectedPeriodOption.label,
                },
              )}
            </StyledInfoText>
            <Spacer y={1.5} />
          </>
        )}
        {!hasRecipients ? (
          <MessageTooltip
            type="WARNING"
            iconLeft={<WarningOutlined />}
            title={t("fleet.monthlyReports.generateModal.noRecipients.title")}
            text={
              <>
                {t(
                  "fleet.monthlyReports.generateModal.noRecipients.bodyBeforeLink",
                )}
                <StyledLink href="/home/users/management">
                  {t("fleet.monthlyReports.recipientsBanner.link")}
                </StyledLink>
                {t(
                  "fleet.monthlyReports.generateModal.noRecipients.bodyAfterLink",
                )}
              </>
            }
          />
        ) : (
          <StyledInfoText>
            <Spacer x={0.25} />
            {t(
              reportRecipientsCount === 1
                ? "fleet.monthlyReports.generateModal.recipientsCountSingular"
                : "fleet.monthlyReports.generateModal.recipientsCount",
              { count: reportRecipientsCount },
            )}
          </StyledInfoText>
        )}
      </StyledColumn>
    </DialogModalDS>
  );
};

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledInfoText = styled.div`
  display: flex;
  ${typographies["Body/S"]};
  color: ${colors["colors/text/darkGrey"]};
`;

const StyledLink = styled.a`
  text-decoration: underline;
  color: inherit;
`;
